import type { FC } from "react";

import { OTP_METHODS, type OtpMethod } from "../constants/otp";
import { assertNever } from "../helpers/assertNever";
import type { OtpLoginEmailTranslations } from "../pages/otp-login-email";
import type { OtpLoginPhoneTranslations } from "../pages/otp-login-phone";
import { type Translate, useSimpleTranslate } from "../SimpleTranslateProvider";
import { OTPPlainButton } from "./OTPPlainButton";

interface OtpResendPasscodeButtonProps {
  handleOTPTokenResend: () => void;
  isResendingOtpToken: boolean;
  otpMethod: OtpMethod;
}

/**
 * Post-MVP TODO: Consider refactoring to use new (still in-progress) Pedal Alert Banner
 *
 * @see https://team-turo.atlassian.net/browse/SCHU-13635
 */
export const getOtpResendPasscodeButtonCopy = ({
  isResendingOtpToken,
  otpMethod,
  translate,
}: Omit<OtpResendPasscodeButtonProps, "handleOTPTokenResend"> & {
  translate: Translate<OtpLoginEmailTranslations & OtpLoginPhoneTranslations>;
}) => {
  if (otpMethod === OTP_METHODS.SMS) {
    if (isResendingOtpToken) {
      return translate(
        "auth.login.otp.phone.passcode.form.resend.link_text.resending",
      );
    }

    return translate("auth.login.otp.phone.passcode.form.resend.link_text");
  }

  if (otpMethod === OTP_METHODS.EMAIL) {
    if (isResendingOtpToken) {
      return translate(
        "auth.login.otp.email.passcode.resend.link_text.resending",
      );
    }
    return translate("auth.login.otp.email.passcode.resend.link_text");
  }

  return assertNever(otpMethod);
};

export const OtpResendPasscodeButton: FC<OtpResendPasscodeButtonProps> = ({
  handleOTPTokenResend,
  isResendingOtpToken,
  otpMethod,
}) => {
  const translate = useSimpleTranslate<
    OtpLoginEmailTranslations & OtpLoginPhoneTranslations
  >();
  const copy = getOtpResendPasscodeButtonCopy({
    isResendingOtpToken,
    otpMethod,
    translate,
  });

  return (
    <OTPPlainButton
      isDisabled={isResendingOtpToken}
      onClick={handleOTPTokenResend}
      shouldHugContent
    >
      {copy}
    </OTPPlainButton>
  );
};
